// What a building leaves behind: a rubble mound over its footprint that smoulders for a while, and the
// low dust wave that rolls out of a collapse. Both worlds use them (the mound covers the photo world's
// flattened foundation slab; in Classic it sits where the shell sank).
import * as THREE from 'three';
import { scene } from './scene';
import { dust } from './particles';
import { CHAR } from './facades';
import { clamp, rnd } from './util';

function rubbleTexture(){
  const c = document.createElement('canvas'); c.width = c.height = 256; const g = c.getContext('2d')!;
  g.fillStyle = '#5a5552'; g.fillRect(0, 0, 256, 256);
  for (let i = 0; i < 2600; i++){ const s = 2 + Math.random() * 9; const v = 40 + Math.random() * 70; g.fillStyle = `rgba(${v},${v - 4},${v - 8},${0.55 + Math.random() * 0.45})`; g.fillRect(Math.random() * 256, Math.random() * 256, s, s * (0.4 + Math.random())); }
  for (let i = 0; i < 700; i++){ g.fillStyle = `rgba(0,0,0,${0.15 + Math.random() * 0.4})`; g.fillRect(Math.random() * 256, Math.random() * 256, 1.5 + Math.random() * 4, 1.5); }
  for (let i = 0; i < 260; i++){ g.fillStyle = `rgba(255,236,210,${0.08 + Math.random() * 0.2})`; g.fillRect(Math.random() * 256, Math.random() * 256, 1 + Math.random() * 3, 1 + Math.random() * 2); }
  const t = new THREE.CanvasTexture(c); t.wrapS = t.wrapT = THREE.RepeatWrapping; t.colorSpace = THREE.SRGBColorSpace; t.anisotropy = 4; return t;
}
let rubbleTex: THREE.Texture | null = null;
export function getRubbleTex(){ return rubbleTex = rubbleTex || rubbleTexture(); }
const EMBER = new THREE.Color('#ff4a1e');
export const ruins: Array<{ mesh: THREE.Mesh; born: number; mat: THREE.MeshStandardMaterial; x: number; z: number; r: number; smoke: number }> = [];
const MAX_RUINS = 140;

// old = destroyed before we arrived: cold, no smoke
export function addRuin(u: any, gy: number, tint: THREE.Color | null, old = false){
  rubbleTex = getRubbleTex();
  const mound = clamp(2.2 + u.h * 0.045, 2.2, 9);
  const cx = u.center.x, cz = u.center.z;
  const shape = new THREE.Shape(u.pts.map((p: number[]) => new THREE.Vector2((p[0] - cx) * 0.92, -(p[1] - cz) * 0.92)));
  let geo: THREE.BufferGeometry;
  try { geo = new THREE.ExtrudeGeometry(shape, { depth: mound, bevelEnabled: true, bevelThickness: mound * 0.9, bevelSize: Math.min(4, u.fr * 0.25), bevelSegments: 2, curveSegments: 6 }); }
  catch (e) { return; }
  geo.rotateX(-Math.PI / 2);
  // rough it up: jitter every vertex above the base so the heap reads as broken slabs, not an extrusion
  const pos = geo.attributes.position as THREE.BufferAttribute;
  for (let i = 0; i < pos.count; i++){ const y = pos.getY(i); if (y > 0.3){ pos.setXYZ(i, pos.getX(i) + rnd(-1.6, 1.6), y * rnd(0.55, 1.25) + rnd(-0.4, 0.9), pos.getZ(i) + rnd(-1.6, 1.6)); } }
  geo.computeVertexNormals();
  // concrete grey pulled a little toward the facade's colour; the ember glow is faint and dies in seconds
  const col = tint ? new THREE.Color('#6a645f').lerp(tint, 0.35).lerp(CHAR, 0.25) : new THREE.Color('#5e5853');
  const mat = new THREE.MeshStandardMaterial({ color: col, map: rubbleTex, bumpMap: rubbleTex, bumpScale: 1.2, roughness: 1, metalness: 0, flatShading: true, emissive: EMBER, emissiveIntensity: old ? 0 : 0.22 });
  const rep = Math.max(1, Math.round(u.fr / 9)); mat.map!.repeat.set(rep, rep);
  const mesh = new THREE.Mesh(geo, mat); mesh.position.set(cx, gy, cz); mesh.receiveShadow = true; mesh.castShadow = u.fr > 12;
  scene.add(mesh);
  ruins.push({ mesh, born: performance.now() - (old ? 120000 : 0), mat, x: cx, z: cz, r: Math.max(4, u.fr * 0.6), smoke: clamp(u.vol / 90000, 0.2, 1) });
  while (ruins.length > MAX_RUINS){ const r = ruins.shift()!; scene.remove(r.mesh); r.mesh.geometry.dispose(); r.mat.dispose(); }
}
export function updateRuins(now: number){
  for (const r of ruins){
    const age = (now - r.born) / 1000;
    r.mat.emissiveIntensity = 0.22 * Math.exp(-age / 5);
    // a thin, slow smoulder for the first half minute
    if (age < 32 && Math.random() < 0.14 * r.smoke * (1 - age / 32)) smokeRef.emit(_p.set(r.x + rnd(-r.r, r.r), r.mesh.position.y + 3, r.z + rnd(-r.r, r.r)), _v.set(rnd(-1, 1), 4 + Math.random() * 3, rnd(-1, 1)), 2, 7);
  }
}
export function clearRuins(){ for (const r of ruins){ scene.remove(r.mesh); r.mesh.geometry.dispose(); r.mat.dispose(); } ruins.length = 0; }
const _p = new THREE.Vector3(), _v = new THREE.Vector3();
import { smoke as smokeRef } from './particles';

// ── dust wave: a flat, soft disc that rolls out from the footprint at street level
function dustDiscTexture(){
  const c = document.createElement('canvas'); c.width = c.height = 128; const g = c.getContext('2d')!;
  const r = g.createRadialGradient(64, 64, 20, 64, 64, 64); r.addColorStop(0, 'rgba(255,255,255,0)'); r.addColorStop(0.55, 'rgba(255,255,255,.9)'); r.addColorStop(0.8, 'rgba(255,255,255,.55)'); r.addColorStop(1, 'rgba(255,255,255,0)');
  g.fillStyle = r; g.fillRect(0, 0, 128, 128);
  g.globalCompositeOperation = 'destination-out'; for (let i = 0; i < 90; i++){ const a = Math.random() * 6.28, d = 34 + Math.random() * 30; g.beginPath(); g.arc(64 + Math.cos(a) * d, 64 + Math.sin(a) * d, 3 + Math.random() * 9, 0, 7); g.fill(); }
  const t = new THREE.CanvasTexture(c); return t;
}
let discTex: THREE.Texture | null = null;
const discGeo = new THREE.CircleGeometry(1, 40);
const waves: Array<{ m: THREE.Mesh; t: number; r0: number; r1: number; life: number }> = [];
export function dustWave(x: number, z: number, gy: number, r0: number, r1: number, life = 1.7){
  discTex = discTex || dustDiscTexture();
  const m = new THREE.Mesh(discGeo, new THREE.MeshBasicMaterial({ map: discTex, color: '#8a7a68', transparent: true, opacity: 0.6, depthWrite: false, fog: true }));
  m.rotation.x = -Math.PI / 2; m.rotation.z = Math.random() * 6; m.position.set(x, gy + 0.8, z); m.renderOrder = 3; scene.add(m);
  waves.push({ m, t: 0, r0, r1, life });
  // low, fast dust along the perimeter, blowing outward
  for (let i = 0; i < 46; i++){ const a = Math.random() * 6.28, spd = rnd(12, 26); dust.emit(_p.set(x + Math.cos(a) * r0, gy + 1.5, z + Math.sin(a) * r0), _v.set(Math.cos(a) * spd, 3, Math.sin(a) * spd), 5, 2.4); }
}
export function updateDustWaves(dt: number){
  for (let i = waves.length - 1; i >= 0; i--){ const w = waves[i]; w.t += dt; const k = w.t / w.life;
    if (k >= 1){ scene.remove(w.m); (w.m.material as THREE.Material).dispose(); waves.splice(i, 1); continue; }
    const e = 1 - Math.pow(1 - k, 2.2); const rad = w.r0 + (w.r1 - w.r0) * e; w.m.scale.set(rad, rad, 1); (w.m.material as THREE.MeshBasicMaterial).opacity = 0.6 * (1 - k) * (1 - k); }
}
export function clearDustWaves(){ for (const w of waves){ scene.remove(w.m); (w.m.material as THREE.Material).dispose(); } waves.length = 0; }
